'use client';

import { useEffect, useRef } from 'react';
import { getGsap } from '@/lib/gsap';

interface UseGsapRevealOptions {
  /** Starting Y offset in px. Default 40 */
  y?: number;
  /** Starting opacity. Default 0 */
  opacity?: number;
  /** Duration in seconds. Default 0.8 */
  duration?: number;
  /** Stagger delay between children in seconds. Default 0.15 */
  stagger?: number;
  /** ScrollTrigger start position. Default 'top 85%' */
  start?: string;
  /** Whether to animate children instead of the element itself. Default false */
  staggerChildren?: boolean;
  /** Ease function. Default 'power3.out' */
  ease?: string;
}

/**
 * GSAP ScrollTrigger reveal animation.
 * Attach the returned ref to the element you want to animate.
 * Respects prefers-reduced-motion.
 */
export function useGsapReveal<T extends HTMLElement = HTMLDivElement>(
  options: UseGsapRevealOptions = {},
) {
  const {
    y = 40,
    opacity = 0,
    duration = 0.8,
    stagger = 0.15,
    start = 'top 85%',
    staggerChildren = false,
    ease = 'power3.out',
  } = options;

  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    // If the element is already in (or above) the viewport when the hook
    // first runs — typical when the parent waits to mount until scrolled
    // into view — skip the reveal animation. Otherwise gsap.from() applies
    // an opacity:0 from-state and ScrollTrigger never fires (because it's
    // past its trigger point), trapping the content invisible.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) return;

    let ctx: { revert: () => void } | null = null;
    let cancelled = false;

    getGsap().then(({ gsap, ScrollTrigger }) => {
      if (cancelled) return;
      const targets = staggerChildren ? el.children : el;

      ctx = gsap.context(() => {
        gsap.from(targets, {
          y,
          opacity,
          duration,
          stagger: staggerChildren ? stagger : 0,
          ease,
          scrollTrigger: {
            trigger: el,
            start,
            toggleActions: 'play none none none',
          },
        });
      });

      // Recompute trigger positions after async mounts shift the layout.
      ScrollTrigger.refresh();
    });

    return () => {
      cancelled = true;
      ctx?.revert();
    };
  }, [y, opacity, duration, stagger, start, staggerChildren, ease]);

  return ref;
}
